// Google Analytics helper functions for tracking events
// Uses Google Consent Mode V2 compliance
import { consentStillValid, readTrackingConsent, type TrackingCategory, type TrackingConsent } from "./tracking-consent";

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
  }
}

// ── CMS-driven analytics config ────────────────────────────────────────────
// GA4 / Google Ads IDs are never hardcoded in the bundle: they are loaded
// from /api/public/analytics-config (values live in site_settings).
// If the endpoint is unreachable or the IDs are absent, tracking is a no-op.
interface AnalyticsConfig {
  ga4Id: string | null;
  adsId: string | null;
  conversionLabel: string | null;
  metaPixelId: string | null;
  metaPixelEnabled: boolean;
}

const EMPTY_CONFIG: AnalyticsConfig = {
  ga4Id: null, adsId: null, conversionLabel: null, metaPixelId: null, metaPixelEnabled: false,
};

let analyticsConfigPromise: Promise<AnalyticsConfig> | null = null;

export function getAnalyticsConfig(): Promise<AnalyticsConfig> {
  if (!analyticsConfigPromise) {
    analyticsConfigPromise = fetch('/api/public/analytics-config')
      .then((r) => (r.ok ? r.json() : EMPTY_CONFIG))
      .then((cfg) => ({
        ga4Id: cfg?.ga4Id ?? null,
        adsId: cfg?.adsId ?? null,
        conversionLabel: cfg?.conversionLabel ?? null,
        metaPixelId: cfg?.metaPixelId ?? null,
        metaPixelEnabled: cfg?.metaPixelEnabled === true,
      }))
      .catch(() => {
        // Non-fatal: allow a retry on the next tracking call
        analyticsConfigPromise = null;
        return EMPTY_CONFIG;
      });
  }
  return analyticsConfigPromise;
}

async function prepareGoogleEvent(category: TrackingCategory, consent: TrackingConsent) {
  if (typeof window === "undefined" || !consent[category] || typeof window.gtag !== "function") return null;
  const cfg = await getAnalyticsConfig();
  if (!consentStillValid(consent, category)) return null;
  if (!(category === "analytics" ? cfg.ga4Id : cfg.adsId)) return null;
  // Non accodare eventi su uno stub in attesa di un consenso/caricamento futuro.
  let ready = false;
  try { ready = await window.__uoLoadAnalytics?.() === true; } catch { return null; }
  return ready && consentStillValid(consent, category) ? cfg : null;
}

// Track page views (automatically handled by gtag config)
export const trackPageView = (url: string) => {
  const consent = readTrackingConsent();
  prepareGoogleEvent("analytics", consent).then((cfg) => {
    if (cfg?.ga4Id && window.gtag) {
      window.gtag('config', cfg.ga4Id, {
        page_path: url,
      });
    }
  });
};

// Track custom events
export const trackEvent = async (
  eventName: string,
  parameters?: {
    category?: string;
    label?: string;
    value?: number;
    [key: string]: any;
  }
) : Promise<boolean> => {
  const consent = readTrackingConsent();
  const cfg = await prepareGoogleEvent("analytics", consent);
  if (cfg?.ga4Id && typeof window.gtag === "function") {
    window.gtag('event', eventName, {
      event_category: parameters?.category,
      event_label: parameters?.label,
      value: parameters?.value,
      ...parameters,
      send_to: cfg.ga4Id,
    });
    return true;
  }
  return false;
};

// E-commerce tracking functions

// Track product view
export const trackProductView = (product: {
  id: string;
  name: string;
  category: string;
  brand?: string;
  price: number;
}) => {
  trackEvent('view_item', {
    currency: 'EUR',
    value: product.price,
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      item_brand: product.brand,
      price: product.price,
      quantity: 1
    }]
  });
};

// Track add to cart (for future implementation)
export const trackAddToCart = (product: {
  id: string;
  name: string;
  category: string;
  brand?: string;
  price: number;
  quantity?: number;
}) => {
  trackEvent('add_to_cart', {
    currency: 'EUR',
    value: product.price * (product.quantity || 1),
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      item_brand: product.brand,
      price: product.price,
      quantity: product.quantity || 1
    }]
  });
};

// Track form submissions
export const trackFormSubmission = (formName: string, formData?: any) => {
  trackEvent('generate_lead', {
    form_name: formName,
    ...formData
  });
};

// Track contact form
export const trackContactForm = (service: string, userType: string) => {
  trackEvent('contact_form_submit', {
    category: 'engagement',
    label: `${service}_${userType}`,
    service,
    user_type: userType
  });
};

// Track gold evaluation request
export const trackGoldEvaluation = (metalType: string, weight: number, estimatedValue: number) => {
  trackEvent('gold_evaluation', {
    category: 'lead_generation',
    label: metalType,
    value: estimatedValue,
    metal_type: metalType,
    weight_grams: weight
  });
};

// Track watch inquiry
export const trackWatchInquiry = (brand: string, model: string, action: 'sell' | 'buy') => {
  trackEvent('watch_inquiry', {
    category: 'lead_generation',
    label: `${action}_${brand}`,
    watch_brand: brand,
    watch_model: model,
    action
  });
};

// Track investment request
export const trackInvestmentRequest = (productType: string, quantity: number, totalPrice: number) => {
  trackEvent('investment_request', {
    category: 'lead_generation',
    label: productType,
    value: totalPrice,
    product_type: productType,
    quantity
  });
};

// Track WhatsApp click
export const trackWhatsAppClick = (context: string, productInfo?: string) => {
  trackEvent('whatsapp_click', {
    category: 'engagement',
    label: context,
    product_info: productInfo
  });
};

// Track phone call click
export const trackPhoneClick = (context: string) => {
  trackEvent('phone_click', {
    category: 'engagement',
    label: context
  });
};

// Track quotation view
export const trackQuotationView = (metalType: 'gold' | 'silver', price: number) => {
  trackEvent('quotation_view', {
    category: 'engagement',
    label: metalType,
    value: price,
    metal_type: metalType,
    price_per_gram: price
  });
};

// Track service selection
export const trackServiceSelection = (service: string, userType: string) => {
  trackEvent('service_selection', {
    category: 'navigation',
    label: `${service}_${userType}`,
    service,
    user_type: userType
  });
};

// Enhanced E-commerce tracking for conversions

// Track purchase (for future checkout implementation)
export const trackPurchase = (transactionData: {
  transactionId: string;
  totalAmount: number;
  tax?: number;
  shipping?: number;
  items: Array<{
    id: string;
    name: string;
    category: string;
    brand?: string;
    price: number;
    quantity: number;
  }>;
}) => {
  trackEvent('purchase', {
    transaction_id: transactionData.transactionId,
    value: transactionData.totalAmount,
    tax: transactionData.tax || 0,
    shipping: transactionData.shipping || 0,
    currency: 'EUR',
    items: transactionData.items.map(item => ({
      item_id: item.id,
      item_name: item.name,
      item_category: item.category,
      item_brand: item.brand,
      price: item.price,
      quantity: item.quantity
    }))
  });
};

// ── RUM: Core Web Vitals reali (web-vitals → GA4) ─────────────────────────
// Raccoglie LCP, INP, CLS, FCP e TTFB dagli utenti reali e li invia a GA4
// come eventi `web_vitals`, con page_path e device_type come dimensioni.
// Consent-gated: gtag.js viene caricato SOLO dopo il consenso analytics
// (Consent Mode V2, vedi client/index.html). Se il consenso manca, window.gtag
// resta il semplice stub dataLayer e gli eventi non raggiungono mai Google:
// qui aggiungiamo comunque un controllo esplicito sul consenso per non
// accodare dati nel dataLayer senza consenso.
// I valori sono etichettati `collection_method: 'rum'` per distinguerli
// chiaramente dai valori lab di Lighthouse nei report.

function hasAnalyticsConsent(): boolean {
  return readTrackingConsent().analytics;
}

// Funnel events use only controlled enums and opaque IDs. Never send form
// contents, contact details, referrers, query strings or product names.
const contactEvents = new Set<string>();
export function trackContactJourney(
  stage: 'view' | 'start' | 'step' | 'error' | 'submitted' | 'appointment_confirmed' | 'order_completed',
  service: string,
  id?: string,
  step?: 'service' | 'user_type' | 'intent',
): void {
  if (typeof window === 'undefined' || !hasAnalyticsConsent() || typeof window.gtag !== 'function') return;
  const key = `${stage}:${step || ''}:${id || service}`;
  if (contactEvents.has(key)) return;
  // Successes are deduplicated across reloads in this browser session.
  if (id && ['submitted', 'appointment_confirmed', 'order_completed'].includes(stage)) {
    try {
      const stored = `uo_funnel_${key}`;
      if (sessionStorage.getItem(stored)) return;
      sessionStorage.setItem(stored, '1');
    } catch { /* in-memory deduplication still applies */ }
  }
  contactEvents.add(key);
  window.gtag('event', `contact_${stage}`, {
    service,
    ...(step ? { form_step: step } : {}),
    ...(id ? { event_id: id } : {}),
  });
}

function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const ua = navigator.userAgent;
  if (/iPad|Android(?!.*Mobile)|Tablet/i.test(ua)) return 'tablet';
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
  return 'desktop';
}

// ── Release version (per release marker e attribuzione pre/post deploy) ───
// Letta da /api/app-version: { version, commit }. Cache in memoria; il fetch
// avviene una sola volta per sessione. Se l'endpoint non risponde, gli eventi
// partono senza release_version (mai bloccare il RUM per questo).
interface AppVersion {
  version: string | null;
  commit: string | null;
}

let appVersionPromise: Promise<AppVersion> | null = null;

function getAppVersion(): Promise<AppVersion> {
  if (!appVersionPromise) {
    appVersionPromise = fetch('/api/app-version')
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => ({ version: v?.version ?? null, commit: v?.commit ?? null }))
      .catch(() => {
        appVersionPromise = null;
        return { version: null, commit: null };
      });
  }
  return appVersionPromise;
}

// Release marker: la prima volta che un browser (con consenso analytics) vede
// un commit diverso dall'ultimo registrato, invia un evento `deploy_marker`.
// In GA4 questo permette di separare le metriche pre/post ottimizzazione:
// la data del primo deploy_marker per release_version è la data del deploy.
const RELEASE_MARKER_KEY = 'rum_last_release';

function maybeSendDeployMarker(ver: AppVersion): void {
  const release = ver.commit || null;
  if (!release) return;
  try {
    if (localStorage.getItem(RELEASE_MARKER_KEY) === release) return;
  } catch {
    return;
  }
  if (!hasAnalyticsConsent() || typeof window.gtag !== 'function') return;
  getAnalyticsConfig().then((cfg) => {
    if (!cfg.ga4Id || !window.gtag || !hasAnalyticsConsent()) return;
    window.gtag('event', 'deploy_marker', {
      send_to: cfg.ga4Id,
      event_category: 'web_vitals',
      release_version: release,
      release_commit: ver.commit || '',
      deploy_date: new Date().toISOString().slice(0, 10),
      page_path: window.location.pathname,
      collection_method: 'rum',
      non_interaction: true,
    });
    try {
      localStorage.setItem(RELEASE_MARKER_KEY, release);
    } catch {
      // storage pieno/bloccato: al massimo il marker si ripete
    }
  });
}

import { loadCompatibleWebVitals } from "./web-vitals-compat";

let webVitalsInitialized = false;

export function initWebVitals(): void {
  if (typeof window === 'undefined' || webVitalsInitialized) return;
  webVitalsInitialized = true;

  // Import dinamico del build con ATTRIBUTION (~+1.5KB, lazy: non pesa sul
  // bundle critico). L'attribution fornisce l'elemento LCP, il target INP e
  // la sorgente del layout shift — indispensabili per diagnosi per route.
  loadCompatibleWebVitals()
    .then((metrics) => {
      if (!metrics) return; // Optional RUM requires Array.at; never patch browser globals.
      const { onLCP, onINP, onCLS, onFCP, onTTFB } = metrics;
      const report = (metric: {
        name: string;
        value: number;
        delta: number;
        id: string;
        rating: string;
        navigationType?: string;
        attribution?: Record<string, any>;
      }) => {
        if (!hasAnalyticsConsent()) return;
        if (typeof window.gtag !== 'function') return;
        Promise.all([getAnalyticsConfig(), getAppVersion()]).then(([cfg, ver]) => {
          if (!cfg.ga4Id || !window.gtag || !hasAnalyticsConsent()) return;
          // CLS è un rapporto (moltiplicato ×1000 per avere un intero utile
          // in GA4); le altre metriche sono in millisecondi.
          const value = Math.round(
            metric.name === 'CLS' ? metric.delta * 1000 : metric.delta
          );
          // Attribuzione (solo selettori CSS / nomi risorsa — mai dati
          // personali): elemento LCP, target dell'interazione INP, sorgente
          // del layout shift CLS. debug_target è il campo "principale" per
          // ciascuna metrica (convenzione GA4/web-vitals).
          const a = metric.attribution || {};
          const lcpElement = metric.name === 'LCP' ? a.element || '' : '';
          const inpElement =
            metric.name === 'INP' ? a.interactionTarget || '' : '';
          const clsSource =
            metric.name === 'CLS' ? a.largestShiftTarget || '' : '';
          const debugTarget = lcpElement || inpElement || clsSource || '';
          window.gtag('event', 'web_vitals', {
            send_to: cfg.ga4Id,
            event_category: 'web_vitals',
            event_label: metric.id, // raggruppa i delta della stessa metrica
            value,
            metric_name: metric.name,
            metric_value: metric.value,
            metric_delta: metric.delta,
            metric_id: metric.id,
            metric_rating: metric.rating,
            navigation_type: metric.navigationType || '',
            page_path: window.location.pathname,
            device_type: getDeviceType(),
            lcp_element: lcpElement,
            inp_element: inpElement,
            cls_source: clsSource,
            debug_target: debugTarget,
            release_version: ver.commit || ver.version || '',
            collection_method: 'rum', // distingue RUM dai valori lab Lighthouse
            non_interaction: true, // non incide sul bounce rate
          });
          maybeSendDeployMarker(ver);
        });
      };

      onLCP(report);
      onINP(report);
      onCLS(report);
      onFCP(report);
      onTTFB(report);
    })
    .catch(() => {
      // Non-fatale: la raccolta RUM non deve mai rompere l'app.
      webVitalsInitialized = false;
    });
}

// Track conversion for Google Ads
// NOTE: le conversioni Ads vanno inviate all'ID Google Ads (AW-...), NON all'ID GA4 (G-...)
// Sia l'ID Ads che il conversion label sono letti dal CMS via
// /api/public/analytics-config: se assenti, no-op. È possibile passare un
// label esplicito per conversioni diverse da quella di default.
export const trackConversion = async (
  value: number, labelOverride?: string, transactionId?: string,
): Promise<boolean> => {
  const consent = readTrackingConsent();
  const cfg = await prepareGoogleEvent("marketing", consent);
  const label = labelOverride || cfg?.conversionLabel;
  if (!cfg?.adsId || !label || typeof window.gtag !== "function") return false;
  window.gtag('event', 'conversion', {
    send_to: `${cfg.adsId}/${label}`,
    value,
    currency: 'EUR',
    ...(transactionId ? { transaction_id: transactionId } : {}),
  });
  return true;
};